/**
 * Certificates — the admin side that configures one, and the learner side that holds them.
 *
 *   READ  (admin)    GET    /api/contentstore/v1/certificates/<course_key>
 *   WRITE (admin)    PUT    /api/authoring/v1/tenants/<slug>/certificates/<course_key>
 *                    POST   /api/authoring/v1/tenants/<slug>/certificates/<course_key>  { is_active }
 *                    DELETE /api/authoring/v1/tenants/<slug>/certificates/<course_key>
 *   READ  (learner)  GET    /api/certificates/v0/certificates/<username>/
 *
 * WHY THE WRITES ARE OURS AND THE READS ARE THE PLATFORM'S
 * -------------------------------------------------------
 * Studio does serve these writes. `frontend-app-authoring` calls them at
 * `POST {STUDIO}/certificates/{courseId}[/{id}]` and
 * `POST {STUDIO}/certificates/activation/{courseId}/` — bare Studio paths with no `/api`
 * prefix. That prefix cannot be proxied to the CMS, because `/certificates/<uuid>` on the
 * LMS is where a learner's issued certificate is served: one path, two services, opposite
 * meanings. So the writes are wrapped in our authoring app, which adds the org-scoped
 * tenant check the platform has no concept of.
 *
 * The read needs no wrapper. `GET /api/contentstore/v1/certificates/{course_key}` is DRF,
 * already proxied, and returns more than a certificate list — the course's enrolment modes,
 * the activation state and the preview URL. It stays where it is; see
 * `studio-settings.ts::getCourseCertificates`.
 *
 * WHAT THE LEARNER LIST IS, AND IS NOT
 * ------------------------------------
 * `/api/certificates/v0/certificates/<username>/` returns only certificates the learner may
 * actually see. The platform filters it three ways before answering — passing status,
 * `certificates_viewable_for_course` (the course's display behaviour), and
 * `has_any_active_web_certificate` (an ACTIVE certificate configuration on the course). A
 * certificate that exists but is not yet showable is therefore ABSENT rather than listed as
 * pending, which is why the page reads the dashboard's per-course state as well: that is
 * where "earned, not yet available" lives.
 */

import { ApiError, openedx, request } from './openedx';

/* ------------------------------------------------------------------ admin writes ---- */

/** One signature line on the certificate. Matches the platform's `SignatorySerializer`. */
export interface CertificateSignatoryInput {
  /**
   * Present only for a signatory the platform has already stored.
   *
   * Rows added in the browser have no id, and must not invent one: Studio's own MFE
   * generates `local-<uuid>` placeholders and strips them before sending, because
   * `CertificateManager.assign_id` treats whatever arrives as an assigned identifier. Our
   * endpoint drops anything that is not an integer for the same reason.
   */
  id?: number;
  name: string;
  title: string;
  organization: string;
  signature_image_path: string;
}

export interface CertificateInput {
  name: string;
  description: string;
  /** Overrides the course's own title ON THE CERTIFICATE only. Blank means use the course's. */
  course_title: string;
  is_active: boolean;
  signatories: CertificateSignatoryInput[];
  /**
   * Whether a learner sees the certificate as soon as they earn it.
   *
   * A separate question from whether one is issued, and the default answer is a trap:
   * `certificates_display_behavior` defaults to "end", which the platform resolves against
   * the course's end date — so a course with no end date generates certificates and shows
   * them to nobody, permanently. Sent explicitly so the choice is visible; omitted (or
   * `null`) leaves whatever the course already says.
   */
  show_as_soon_as_earned?: boolean | null;
}

/** What the server stores. Same shape the contentstore read returns per certificate. */
export interface SavedCertificate {
  id: number;
  name: string;
  description: string;
  course_title?: string;
  is_active: boolean;
  version: number;
  signatories: Array<CertificateSignatoryInput & { id: number }>;
}

const adminBase = (slug: string, courseKey: string) =>
  `/api/authoring/v1/tenants/${encodeURIComponent(slug)}/certificates/${encodeURIComponent(courseKey)}`;

/**
 * Create or replace this course's certificate.
 *
 * The WHOLE object goes every time. `course.certificates` is a single JSON field and the
 * platform's serializer merges what it is given onto what is there, so a partial payload
 * leaves the previous signatories attached to a new title — a certificate that is half of
 * two different ones.
 *
 * `request` rather than a verb helper: the shared client exposes get/post/patch/delete and
 * not put, and adding one there for a single caller is a change to a file every surface
 * depends on. `request` is the same code path those helpers use.
 */
export const saveCourseCertificate = (slug: string, courseKey: string, input: CertificateInput) =>
  request<SavedCertificate>(adminBase(slug, courseKey), { method: 'PUT', json: input });

/** Switch the certificate on or off without touching its content. */
export const setCourseCertificateActive = (slug: string, courseKey: string, isActive: boolean) =>
  openedx.post<{ is_active: boolean }>(adminBase(slug, courseKey), { is_active: isActive });

/** Remove the certificate, and the signature images that belong to it. */
export const deleteCourseCertificate = (slug: string, courseKey: string) =>
  openedx.delete<void>(adminBase(slug, courseKey));

/* --------------------------------------------------------------- learner's own ----- */

/**
 * One certificate a learner holds. Field names are the platform's, unchanged.
 *
 * `download_url` is named after the PDF certificates the platform used to generate, but it is
 * NOT empty on a web-certificate install: the serializer fills it from `get_certificate_url`,
 * which returns the relative path of the web certificate (`/certificates/<uuid>`). Measured,
 * not assumed — a link built from it needs `certificateViewUrl`, not the raw value.
 */
export interface EarnedCertificate {
  username: string;
  course_id: string;
  course_display_name: string;
  course_organization: string;
  /** 'honor' | 'verified' | 'professional' — the enrolment mode it was earned in. */
  certificate_type: string;
  created_date: string;
  modified_date: string;
  status: string;
  is_passing: boolean;
  download_url: string | null;
  /** A float as a string, e.g. "1.0". Empty when the course grades nothing. */
  grade: string;
  /** 32 hex characters. The certificate's public address. */
  uuid: string;
}

/**
 * The learner's certificates.
 *
 * The trailing slash is required — `^{username}/$` — and without it Django's APPEND_SLASH
 * turns the GET into a redirect that drops the credentials on the way through the proxy.
 */
export const listEarnedCertificates = (username: string) =>
  openedx.get<EarnedCertificate[]>(
    `/api/certificates/v0/certificates/${encodeURIComponent(username)}/`,
  );

/**
 * Where a certificate is rendered.
 *
 * SAME-ORIGIN, and a relative `download_url` is left relative on purpose. The path
 * `/certificates/<32 hex>` is proxied to the LMS (see `vite.config.ts`), so the platform's
 * own certificate page is served under the tenant's host — which is also how the platform
 * decides whose branding the certificate carries.
 *
 * Relative-vs-absolute is the whole reason this is a function. `download_url` is documented
 * as the PDF download and reads like an external URL, but on a web-certificate install the
 * API fills it from `get_certificate_url`, which is a Django `reverse()` — so it arrives as
 * `/certificates/<uuid>`, with no host. Returning it unexamined was a dead link for every
 * certificate: relative to OUR origin it hit the SPA router, which has a `/certificates` list
 * route and nothing under it, and rendered a 404 page. Only a genuinely absolute value (a
 * legacy PDF in external storage) is passed through as given.
 */
export function certificateViewUrl(certificate: Pick<EarnedCertificate, 'uuid' | 'download_url'>) {
  // Both forms are already correct to navigate to: the relative one because the path is
  // proxied, the absolute one because it names its own host. What they are NOT is a route —
  // every link built from this must be a plain `<a>`, never a `<Link>`, or the router
  // answers it instead of the platform.
  return certificate.download_url || (certificate.uuid ? `/certificates/${certificate.uuid}` : null);
}

/** Grade as a percentage, or null when the course grades nothing. */
export function gradePercent(grade: string | null | undefined): number | null {
  if (!grade) return null;
  const value = Number.parseFloat(grade);
  return Number.isFinite(value) ? Math.round(value * 100) : null;
}

/**
 * Certificates grouped by the year they were issued, newest year first.
 *
 * Grouping is presentational, but it belongs here rather than in the page: "which year is
 * this certificate from" is a fact about the data, and a page that re-derives it from a
 * date string is a page that can disagree with the next one.
 */
export function byYear(certificates: EarnedCertificate[]): Array<[string, EarnedCertificate[]]> {
  const groups = new Map<string, EarnedCertificate[]>();
  for (const certificate of certificates) {
    const parsed = Date.parse(certificate.created_date);
    const year = Number.isNaN(parsed) ? 'Undated' : String(new Date(parsed).getFullYear());
    const bucket = groups.get(year);
    if (bucket) bucket.push(certificate);
    else groups.set(year, [certificate]);
  }
  return [...groups.entries()].sort((a, b) => b[0].localeCompare(a[0]));
}

/** "18 April 2026", or the raw value when it will not parse. */
export function formatIssued(value: string | null | undefined): string {
  if (!value) return 'Date unknown';
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) return value;
  return new Date(parsed).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/* -------------------------------------------------------------------- plumbing ----- */

/**
 * Turn a certificate write failure into something an admin can act on.
 *
 * Distinct from `studio-settings.ts::explainFailure` because the failures are different
 * ones: this endpoint refuses on tenancy (404 for a course in another academy, so the
 * existence of other academies' courses is not confirmed) and on author access (403), and
 * returns `detail` rather than contentstore's `error`.
 */
export function explainCertificateFailure(error: unknown): string {
  if (!(error instanceof ApiError)) {
    return 'The request never reached Studio. Check your connection, then try again — nothing was saved.';
  }

  const body = error.body as { detail?: unknown; error?: unknown } | null;
  const detail =
    typeof body?.detail === 'string' ? body.detail
    : typeof body?.error === 'string' ? body.error
    : null;

  switch (error.status) {
    case 400:
      return detail ?? 'Studio rejected the certificate. Check the name and description, then save again.';
    case 403:
      return (
        detail ??
        'You do not have author access to this course. Ask an academy administrator to give you access to it.'
      );
    case 404:
      return detail ?? 'This course is not in this academy, or it no longer exists.';
    case 501:
      return 'Certificates can only be configured through Studio, which is not reachable from here.';
    default:
      return detail ?? `Studio responded with ${error.status}. Nothing was saved.`;
  }
}
